import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr, API_BASE } from "@/lib/api";
import { APP_NAME } from "@/config";
import { cn } from "@/lib/utils";
import { Card, SectionTitle, Segmented, StatusBadge, Label11, int, plural, shortDate } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, FileSignature, Copy } from "lucide-react";

// Investigate, part 5 (backend/incident_reports.py, docs/INVESTIGATE_PLAN.md "Incident reports"): signed reports of
// the session. The founder makes one (the whole session or some turns); the founder and the independent professional
// list and download them. A report is the session as metadata, signed over its exact bytes: anyone can check that
// nothing in it changed since it was made, with the published key and the checker. Never "proof" of more than that.

const SCOPES = [["all", "Whole session"], ["turns", "Some turns"]];
const GRID = "min-w-[720px] grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_80px_minmax(0,1fr)_120px_112px] gap-3 items-center px-4";

const covers = (scope) =>
  scope?.from_turn == null && scope?.to_turn == null ? "Whole session"
    : scope.from_turn === scope.to_turn ? `Turn ${scope.from_turn}`
      : `Turns ${scope.from_turn ?? 1}–${scope.to_turn ?? "last"}`;

// the file as it was signed: payload and signature, nothing added or reformatted inside the payload
function save(envelope, shortId, createdAt) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([JSON.stringify(envelope)], { type: "application/json" }));
  a.download = `${APP_NAME.toLowerCase()}-incident-${shortId}-${(createdAt || "").slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(a.href);
}

async function copy(text, what) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied.`);
  } catch {
    toast.error("Couldn't copy: select it and copy it instead.");
  }
}

// `audience`: "owner" makes reports, "coder" reads them; `names`: user id -> name, for who made each (founders only)
export default function InvestigateReports({ sid, shortId, turns, audience, names }) {
  const qc = useQueryClient();
  const reports = useQuery({
    queryKey: ["incident-reports", sid],
    queryFn: async () => (await api.get(`/sessions/${sid}/incident-reports`)).data,
  });
  const key = useQuery({
    queryKey: ["signing-key"], staleTime: Infinity,
    queryFn: async () => (await api.get("/investigate/signing-key")).data,
    enabled: !!reports.data?.enabled,
  });
  const [scope, setScope] = useState("all");
  const [from, setFrom] = useState("1");
  const [to, setTo] = useState(String(turns || 1));
  const range = scope === "turns" ? { from_turn: Number(from), to_turn: Number(to) } : {};
  const rangeError = scope !== "turns" ? null
    : !(Number.isInteger(range.from_turn) && Number.isInteger(range.to_turn)) || range.from_turn < 1 || range.to_turn < 1
      ? "Turns are numbered from 1." : range.from_turn > range.to_turn ? "The first turn comes after the last one."
        : turns && range.to_turn > turns ? `This session has ${plural(turns, "turn")}.` : null;
  const make = useMutation({
    mutationFn: async () => (await api.post(`/sessions/${sid}/incident-reports`, range)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["incident-reports", sid] });
      toast.success("Report made and signed. Download it below.");
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const [downloading, setDownloading] = useState(null);
  const download = async (r) => {
    setDownloading(r.id);
    try {
      const { envelope } = (await api.get(`/incident-reports/${r.id}`)).data;
      save(envelope, shortId, r.created_at);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setDownloading(null);
    }
  };

  const list = reports.data?.reports || [];
  const current = (key.data?.keys || []).find((k) => k.current);
  const command = `python verify_incident_report.py report.json --url ${API_BASE.replace(/\/api$/, "")}`;
  const owner = audience === "owner";

  return (
    <section className="flex flex-col gap-3" aria-labelledby="reports-title" data-testid="incident-reports">
      <SectionTitle note="Signed: anyone can check that nothing in one changed since it was made.">
        <span id="reports-title">Incident reports</span>
      </SectionTitle>
      <Card className="p-0 overflow-hidden flex flex-col">
        {reports.isLoading ? (
          <p className="m-0 px-4 py-3 text-muted-foreground">Loading…</p>
        ) : reports.error ? (
          <p className="m-0 px-4 py-3 text-muted-foreground">{apiErr(reports.error)}</p>
        ) : !reports.data.enabled ? (
          <p className="m-0 px-4 py-3 text-muted-foreground [text-wrap:pretty]" data-testid="reports-off">
            Incident reports aren't switched on here yet: the platform needs a signing key. {list.length > 0 && "Earlier reports are below."}
          </p>
        ) : owner ? (
          <div className="flex flex-col gap-3 px-4 py-3 border-b" data-testid="report-make">
            <p className="m-0 text-muted-foreground [text-wrap:pretty]">
              A report holds what this page shows, as {APP_NAME} recorded it: the numbers, findings, events, the log and
              integrity checks, the audit entries about the session and its latest change report. Never what was said
              or what a command contained. Nobody can edit or delete one, you included.
            </p>
            <div className="flex flex-wrap items-end gap-3">
              <Segmented label="Covers" options={SCOPES} value={scope} onChange={setScope} testid="report-scope" />
              {scope === "turns" && (
                <span className="flex items-end gap-2">
                  <label className="flex flex-col gap-1"><Label11>From turn</Label11>
                    <Input id="report-from" type="number" min={1} max={turns || undefined} value={from}
                      onChange={(e) => setFrom(e.target.value)} className="w-24 font-mono" data-testid="report-from" />
                  </label>
                  <label className="flex flex-col gap-1"><Label11>To turn</Label11>
                    <Input id="report-to" type="number" min={1} max={turns || undefined} value={to}
                      onChange={(e) => setTo(e.target.value)} className="w-24 font-mono" data-testid="report-to" />
                  </label>
                </span>
              )}
              <Button onClick={() => make.mutate()} disabled={make.isPending || !!rangeError} data-testid="report-make-button">
                <FileSignature />{make.isPending ? "Making the report…" : "Make a report"}
              </Button>
            </div>
            {rangeError && <span className="text-sm text-gr-danger" role="alert">{rangeError}</span>}
          </div>
        ) : null}

        {list.length > 0 ? (
          <div className="overflow-x-auto text-sm tabular-nums" data-testid="report-list">
            <div className={cn(GRID, "py-2.5 border-b")}>
              <Label11>Made</Label11><Label11>Covers</Label11><Label11 className="text-right">Events</Label11>
              <Label11>Checks then</Label11><Label11>Key</Label11><span />
            </div>
            {list.map((r) => (
              <div key={r.id} className={cn(GRID, "py-2.5 border-b last:border-b-0")} data-testid={`report-${r.id}`}>
                <span className="flex flex-col min-w-0">
                  <span>{shortDate(r.created_at, true)}</span>
                  <span className="text-muted-foreground truncate">
                    {owner ? `by ${names?.[r.created_by] || "a founder"}` : "by your founder"}
                  </span>
                </span>
                <span>{covers(r.scope)}</span>
                <span className="text-right font-mono">{int(r.events)}</span>
                <span className="flex flex-wrap gap-1.5">
                  <StatusBadge label={r.chain_valid ? "Log whole" : "Log not whole"} tone={r.chain_valid ? "info" : "danger"} />
                  <StatusBadge label={r.integrity_ok ? "Checks passed" : "Problems"} tone={r.integrity_ok ? "info" : "danger"} />
                </span>
                <span className="font-mono text-muted-foreground">{r.key_id}</span>
                <Button variant="outline" size="sm" className="justify-self-end" onClick={() => download(r)}
                  disabled={downloading === r.id} data-testid={`report-download-${r.id}`}>
                  <Download />{downloading === r.id ? "Saving…" : "Download"}
                </Button>
              </div>
            ))}
          </div>
        ) : reports.data?.enabled && (
          <p className="m-0 px-4 py-3 text-muted-foreground" data-testid="reports-none">
            {owner ? "No reports yet." : "Your founder hasn't made a report of this session."}
          </p>
        )}

        {reports.data?.enabled && (
          <div className="flex flex-col gap-2 px-4 py-3 border-t text-sm text-muted-foreground" data-testid="report-how">
            <span className="font-semibold text-foreground">How to check a report</span>
            <span className="[text-wrap:pretty]">
              Its signature is Ed25519 over the report's exact bytes{current && <>, made with key <span className="font-mono text-foreground">{current.key_id}</span></>}.
              Get the <a className="underline underline-offset-2 hover:text-foreground" href={`${API_BASE}/investigate/checker`}>checker</a> (Python
              and the cryptography package) and run:
            </span>
            <span className="flex items-center gap-2 min-w-0">
              <code className="flex-1 min-w-0 rounded bg-gr-surface-2 px-2 py-1.5 font-mono text-xs text-foreground [overflow-wrap:anywhere]">{command}</code>
              <Button variant="outline" size="sm" onClick={() => copy(command, "Command")} aria-label="Copy the command"><Copy /></Button>
            </span>
            <span className="[text-wrap:pretty]">
              It checks against the <a className="underline underline-offset-2 hover:text-foreground" href={`${API_BASE}/investigate/signing-key`}
                target="_blank" rel="noreferrer">published key</a>, never only the one written inside the report.
            </span>
          </div>
        )}
      </Card>
    </section>
  );
}
